/**
 * Marketing Settings — /marketing/settings
 *
 * Home for marketing-wide admin knobs. Currently hosts:
 *   • Conversion URL patterns — URL substrings that trigger the
 *     book-a-demo CRM cascade (lead_status→hot, crm_lead_events entry,
 *     follow-up note, owner notification) when a recipient clicks.
 *     Previously env-only; now editable via this UI without a redeploy.
 *
 * Future additions (tabs or sections inside this page):
 *   • Kill-switch thresholds (bounce rate, spam rate)
 *   • Digest recipients + hour
 *   • Default throttle overrides
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowsClockwise, Link as LinkIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function MarketingSettingsPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "platform_admin";

  if (!isAdmin) {
    return (
      <div className="max-w-[1100px] mx-auto p-6">
        <div className="bg-white border border-[#E5E5E5] p-6 text-sm text-[#DC2626]">
          Platform admin required.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto p-6 space-y-6" data-testid="marketing-settings-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Settings</p>
        <h1 className="text-xl font-black tracking-tight text-[#0A0A0A]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Marketing admin
        </h1>
      </div>
      <ConversionPatternsCard />
    </div>
  );
}

function ConversionPatternsCard() {
  const [loading, setLoading] = useState(true);
  const [patterns, setPatterns] = useState([]);
  const [envFallback, setEnvFallback] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/conversion-patterns");
      setPatterns(r.data?.patterns || []);
      setEnvFallback((r.data?.env_fallback || []).filter(Boolean));
    } catch (e) {
      toast.error(`Could not load patterns: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addPattern = async () => {
    const url = newUrl.trim();
    if (url.length < 3) {
      toast.error("Pattern must be at least 3 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/marketing/conversion-patterns", {
        url_substring: url,
        label: newLabel.trim() || undefined,
      });
      setNewUrl("");
      setNewLabel("");
      toast.success("Pattern added — live immediately on the next click event.");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(msg.includes("409") ? "This pattern already exists." : `Add failed: ${msg}`);
    } finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/marketing/conversion-patterns/${p.pattern_id}`,
                      { is_active: !p.is_active });
      toast.success(p.is_active ? "Pattern disabled." : "Pattern re-enabled.");
      load();
    } catch (e) {
      toast.error(`Toggle failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deletePattern = async (p) => {
    if (!window.confirm(`Delete pattern "${p.url_substring}"?\n\nClicks on this URL will stop firing the book-a-demo CRM cascade (existing lead events are NOT deleted).`)) return;
    try {
      await api.delete(`/marketing/conversion-patterns/${p.pattern_id}`);
      toast.success("Pattern deleted.");
      load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="conversion-patterns-card">
      <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-baseline justify-between">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
            Book-a-demo trigger · Conversion URL patterns
          </h2>
          <p className="text-[11px] text-[#A3A3A3] mt-1 max-w-2xl">
            When a recipient clicks a link that matches ANY pattern below, Pulse automatically:
            promotes the CRM lead to <strong>hot</strong>, logs a conversion-click event, creates a
            follow-up note due tomorrow, and notifies the assigned account owner. Substring match, case-insensitive.
          </p>
        </div>
        <button type="button" onClick={load}
                className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] flex items-center gap-1"
                data-testid="patterns-reload">
          <ArrowsClockwise size={11} weight="bold" /> Reload
        </button>
      </div>

      {/* Add row */}
      <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">Add pattern</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newUrl}
            onChange={e => setNewUrl(e.target.value)}
            placeholder="e.g. scheduler.zoom.us/eyvo/discovery-call"
            className="flex-1 border border-[#E5E5E5] h-9 px-3 text-sm"
            data-testid="patterns-new-url"
          />
          <input
            type="text"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder="Label (optional)"
            className="w-[220px] border border-[#E5E5E5] h-9 px-3 text-sm"
            data-testid="patterns-new-label"
          />
          <button
            type="button"
            onClick={addPattern}
            disabled={saving || newUrl.trim().length < 3}
            className="h-9 px-4 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40"
            data-testid="patterns-add-btn"
          >
            <Plus size={12} weight="bold" /> Add
          </button>
        </div>
      </div>

      {/* Patterns list */}
      {loading ? (
        <p className="p-6 text-xs text-[#A3A3A3]">Loading patterns…</p>
      ) : patterns.length === 0 ? (
        <div className="p-6 text-center">
          <p className="text-sm text-[#525252] mb-2">No conversion patterns configured yet.</p>
          {envFallback.length > 0 && (
            <p className="text-[11px] text-[#A3A3A3]">
              Env fallback currently supplying: <code>{envFallback.join(", ")}</code>. Add them
              here to make them editable and to get hit-counts.
            </p>
          )}
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="patterns-table">
          <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
            <tr>
              <th className="text-left px-4 py-2 font-bold">URL substring</th>
              <th className="text-left px-3 py-2 font-bold">Label</th>
              <th className="text-right px-3 py-2 font-bold">Hits 30d</th>
              <th className="text-right px-3 py-2 font-bold">Unique ppl</th>
              <th className="text-left px-3 py-2 font-bold">Status</th>
              <th className="text-left px-3 py-2 font-bold">Added by</th>
              <th className="text-right px-4 py-2 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map(p => (
              <tr key={p.pattern_id} className="border-t border-[#F5F5F5]"
                  data-testid={`pattern-row-${p.pattern_id}`}>
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <LinkIcon size={12} weight="bold" className="text-[#A3A3A3]" />
                    <code className="text-xs text-[#0A0A0A]">{p.url_substring}</code>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-xs text-[#525252]">{p.label || "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#0A0A0A] font-bold">
                  {p.hits_30d}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#525252]">
                  {p.unique_clickers_30d}
                </td>
                <td className="px-3 py-2.5">
                  <button type="button" onClick={() => toggleActive(p)}
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${
                            p.is_active
                              ? "bg-[#DCFCE7] text-[#15803D]"
                              : "bg-[#F5F5F5] text-[#A3A3A3]"
                          }`}
                          data-testid={`pattern-toggle-${p.pattern_id}`}>
                    {p.is_active ? "Active" : "Disabled"}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-[10px] text-[#A3A3A3]">{p.created_by || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button type="button" onClick={() => deletePattern(p)}
                          className="p-1 text-[#DC2626] hover:bg-[#FEF2F2]"
                          title="Delete pattern"
                          data-testid={`pattern-delete-${p.pattern_id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
